/**
 * Google Identity Services (GIS) loader + minimal typings for the standalone
 * Google sign-in — the direct Google flow that bypasses Keycloak.
 *
 * We drive it from the app's OWN "Continue with Google" button, so we use the
 * OAuth2 authorization-code flow (`initCodeClient`) rather than the ID-token
 * button widget: a custom button can trigger `requestCode()`, which pops
 * Google's consent and returns an authorization `code`. The backend
 * (`POST /partner/auth/google`) exchanges that code with Google (client id +
 * secret; popup exchanges use redirect_uri `postmessage`) for the user's
 * identity, then issues app tokens.
 *
 * Docs: https://developers.google.com/identity/oauth2/web/guides/use-code-model
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface GisCodeResponse {
  code?: string;
  scope?: string;
  error?: string;
  error_description?: string;
}

interface GisCodeClientConfig {
  client_id: string;
  scope: string;
  ux_mode?: 'popup' | 'redirect';
  redirect_uri?: string;
  callback: (response: GisCodeResponse) => void;
  error_callback?: (error: { type?: string; message?: string }) => void;
}

interface GisCodeClient {
  requestCode: () => void;
}

interface GisNamespace {
  accounts: {
    oauth2: {
      initCodeClient: (config: GisCodeClientConfig) => GisCodeClient;
    };
  };
}

declare global {
  interface Window {
    google?: GisNamespace;
  }
}

let gisPromise: Promise<GisNamespace> | null = null;

/**
 * Load the GIS client script once (promise-cached). Resolves with the `google`
 * namespace. Rejects if the script fails to load (offline / blocked).
 */
export function loadGoogleIdentityServices(): Promise<GisNamespace> {
  if (window.google?.accounts?.oauth2) return Promise.resolve(window.google);
  if (gisPromise) return gisPromise;

  gisPromise = new Promise<GisNamespace>((resolve, reject) => {
    const done = () => {
      if (window.google?.accounts?.oauth2) resolve(window.google);
      else reject(new Error('Google Identity Services loaded but unavailable'));
    };
    const fail = () => {
      gisPromise = null; // allow a later retry
      reject(new Error('Failed to load Google Identity Services'));
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', done);
      existing.addEventListener('error', fail);
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = done;
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return gisPromise;
}

/**
 * Pop Google's consent and resolve with an authorization `code` for the backend
 * to exchange. Rejects if GIS fails to load or the user cancels/closes the popup.
 */
export async function requestGoogleAuthCode(clientId: string): Promise<string> {
  const google = await loadGoogleIdentityServices();
  return new Promise<string>((resolve, reject) => {
    const client = google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: (res) => {
        if (res.error) {
          reject(new Error(res.error_description || res.error));
          return;
        }
        if (res.code) resolve(res.code);
        else reject(new Error('No authorization code returned by Google'));
      },
      error_callback: (err) => {
        reject(new Error(err.message || 'Google sign-in was cancelled.'));
      },
    });
    client.requestCode();
  });
}
